// @file Composable for whiteboard shape handlers
import { ContentPickerSource } from '@@/bits/content_picker'
import { isAppUsing } from '@@/bits/flip'
import {
  DEFAULT_SPEECH_BUBBLE_HEIGHT,
  DEFAULT_SPEECH_BUBBLE_WIDTH,
  LABEL_PADDING,
} from '@@/bits/tldraw/PadletSpeechBubbleUtil'
import { getTextContent } from '@@/bits/tldraw_helpers'
import { UserRole } from '@@/bits/user_model'
import {
  deleteShapeMetadata,
  doesShapeHaveAutoModerationResult,
  freezeAllShapesInGroup,
  isGroupShape,
  isShapeApproved,
  isShapeChildOfGroup,
  isShapeSubjectedToAutoModeration,
  isShapeSubjectedToTextModeration,
  MODERATION_TIMEOUT,
  timeoutShapeModeration,
  unfreezeAllShapesInGroup,
} from '@@/bits/whiteboard'
import { getLinkingFromShape, upsertLinkingToAllShapesInGroup } from '@@/bits/whiteboard_linking'
import { updateSpecialEffectsInAllShapesInGroup } from '@@/bits/whiteboard_special_effects'
import {
  ARROW_TOOL,
  DRAW_NESTED_TOOL,
  DRAW_TOOL,
  findColorMenuItemId,
  GEO_TOOL,
  HIGHLIGHT_TOOL,
  SHAPE_TYPE_TO_TOOL_ID,
} from '@@/bits/whiteboard_tools'
import { ContentModerationMode } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useWhiteboardToolPropertiesStore } from '@@/pinia/useWhiteboardToolPropertiesStore'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import type { ShapeSpecialEffects } from '@@/pinia/whiteboard_special_effects'
import { useWhiteboardSpecialEffectsStore } from '@@/pinia/whiteboard_special_effects'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import { ContentModerationApi } from '@@/surface/api/content_moderation'
import { useWhiteboardPageHandlers } from '@@/vuecomposables/whiteboard_page_handlers'
import { cloneDeep, isEqual } from 'es-toolkit'
import type { Editor, ShapeUtil, TLGeoShape, TLNoteShape, TLShape, TLShapeId, TLUnknownShape } from 'tldraw'
import { ref } from 'vue'

interface InitialShapeMeta {
  cb: number
  ca: number
  lmb: number
  lma: number
  useThemeFont: boolean
  autoSize?: boolean
}

const useWhiteboardShapeHandlers = (editor: Editor): void => {
  const surfaceStore = useSurfaceStore()
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const whiteboardToolsStore = useWhiteboardToolsStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()
  const whiteboardToolbarStore = useWhiteboardToolbarStore()
  const whiteboardSpecialEffectsStore = useWhiteboardSpecialEffectsStore()
  const whiteboardToolPropertiesStore = useWhiteboardToolPropertiesStore()
  const { updatePageAttribution } = useWhiteboardPageHandlers(editor)

  const currentEditingShapeId = ref<string | null>(null)
  const originalShapeTextContent = ref<string | null>(null)
  /**
   * METADATA UPDATES FOR SHAPE CHANGE
   */

  const generateInitialMetaForShape = (shape: TLShape): InitialShapeMeta => {
    const meta: InitialShapeMeta = {
      cb: surfaceStore.user.id, // createdBy
      ca: Date.now(), // createdAt
      lmb: surfaceStore.user.id, // lastModifiedBy
      lma: Date.now(), // lastModifiedAt
      useThemeFont: true,
    }
    if (shape.type === 'text') {
      meta.autoSize = true
    }
    return meta
  }

  const getIsApproved = (shape: TLShape): boolean => {
    const isStickerShape = shape.type === 'image' && shape.meta.contentSource === ContentPickerSource.Stickers
    const isUnmoderatedShape = ['highlight', 'draw', 'image'].includes(shape.type) && !isStickerShape
    const isContentModerationDisabled = surfaceStore.contentModeration === ContentModerationMode.None
    const isContentModerationAuto = surfaceStore.contentModeration === ContentModerationMode.Auto
    const isContentModerationStudentOnly = surfaceStore.contentModeration === ContentModerationMode.StudentsOnly
    const isCurrentUserStudent = surfaceCurrentUserStore.currentUser?.role === UserRole.STUDENT
    const textContent = getTextContent(shape)
    const doesTextModeratedShapeLackText =
      isShapeSubjectedToTextModeration(shape) && (textContent == null || textContent === '')
    const isApproved =
      isUnmoderatedShape ||
      whiteboardPermissionsStore.canIAdminister ||
      isContentModerationDisabled ||
      (isContentModerationStudentOnly && !isCurrentUserStudent) ||
      (isContentModerationAuto && doesTextModeratedShapeLackText) ||
      isGroupShape(shape)

    return isApproved
  }

  const moderationTimeouts = ref<Record<TLShapeId, NodeJS.Timeout | null>>({})

  // Timestamp only exists for for objects waiting for auto moderation
  // This is just an estimate for moderation time out.
  const shouldAddAutoModerationTimestamp = (shape: TLShape): boolean => {
    if (!isShapeSubjectedToAutoModeration(shape)) {
      return false
    }

    return surfaceStore.contentModeration === ContentModerationMode.Auto && !getIsApproved(shape)
  }

  /**
   * If shape is not yet moderated, create a timeout promise to
   * explicitly "timeout" the shape after the TIMEOUT duration
   */
  const startModerationTimeout = async (record: TLShape | null | undefined): Promise<void> => {
    // Ignore if non-admin as the shape won't appear for them
    if (!whiteboardPermissionsStore.canIAdminister) return

    const recordId = record?.id
    if (record == null || recordId == null) return

    // Clear existing timeout if it exists
    const existingTimeout = moderationTimeouts.value[recordId]
    if (existingTimeout != null) {
      clearTimeout(existingTimeout)
      moderationTimeouts.value[recordId] = null
    }

    // Create new timeout
    const timeoutId = setTimeout((): void => {
      timeoutShapeModeration(record)
      moderationTimeouts.value[recordId] = null
    }, MODERATION_TIMEOUT)
    moderationTimeouts.value[recordId] = timeoutId
  }

  /**
   * PERSIST LAST USED TOOL PROPERTIES
   */

  /**
   * Persists tool properties (color, fill style, connector style, etc.) from shape changes to local storage.
   * This allows the tool to remember the last used properties for a better user experience.
   *
   * Note: Font and size changes for text shapes are intentionally NOT persisted
   * to ensure new text objects default to the theme's font instead of the last-used custom font.
   * This behavior is specific to text tool; other tools (shapes, pens) keep their persistent properties.
   */
  function persistToolPropertiesFromShapeChange(toolId: string, prev: TLShape, next: TLShape): void {
    const prevProps = prev.props as Record<string, unknown>
    const nextProps = next.props as Record<string, unknown>
    const properties: Record<string, string> = {}
    let hasChanges = false

    // Detect color changes
    if (prevProps.color !== nextProps.color && typeof nextProps.color === 'string') {
      const colorItemId = findColorMenuItemId(toolId, nextProps.color)
      if (colorItemId != null) {
        properties.color = colorItemId
        hasChanges = true
      }
    }

    // Detect geo shape type changes (geo shapes)
    if (toolId === GEO_TOOL.id && prevProps.geo !== nextProps.geo && typeof nextProps.geo === 'string') {
      properties.geoStyle = nextProps.geo
      hasChanges = true
    }

    // Detect fill style changes (geo shapes)
    if (toolId === GEO_TOOL.id && prevProps.fill !== nextProps.fill && typeof nextProps.fill === 'string') {
      properties.fillStyle = nextProps.fill
      hasChanges = true
    }

    // Detect connector style changes (arrow shapes)
    if (toolId === ARROW_TOOL.id) {
      const dashChanged = prevProps.dash !== nextProps.dash
      const startChanged = prevProps.arrowheadStart !== nextProps.arrowheadStart
      const endChanged = prevProps.arrowheadEnd !== nextProps.arrowheadEnd
      if (dashChanged || startChanged || endChanged) {
        properties.connectorStyle = `${nextProps.arrowheadStart ?? 'none'}-${nextProps.dash ?? 'draw'}-${nextProps.arrowheadEnd ?? 'arrow'}`
        hasChanges = true
      }
    }

    if (!hasChanges) return

    whiteboardToolPropertiesStore.updateToolProperty(toolId, properties)

    // Also update draw-nested when its sub-tools change
    if (toolId === DRAW_TOOL.id || toolId === HIGHLIGHT_TOOL.id) {
      whiteboardToolPropertiesStore.updateToolProperty(DRAW_NESTED_TOOL.id, {
        subTool: toolId,
        ...(properties.color != null ? { color: properties.color } : {}),
      })
    }
  }

  /**
   * Shape creation comes from 2 main use cases:
   *
   * Case 1:
   * General shape creation, initialize meta to mark the shape creator and timestamp.
   *
   * Case 2:
   * When a user copy-pastes or duplicates shapes that they do not own,
   * In this case, we regenerate the initial meta as they should be the owner,
   * otherwise it breaks for certain cases.
   * E.g. Writer duplicates a shape, but writers cannot edit shapes they do not own.
   */
  editor.sideEffects.registerBeforeCreateHandler('shape', (shape, source) => {
    // Setting approval conditionally upon creation accounts for situations like undo/redo,
    // where an admin undos a shape rejection (deletion)
    // and recreating the shape wrongly puts it under the admin's ownership.
    if (shape.meta.cb == null) {
      const updatedShape = {
        ...shape,
        meta: {
          ...shape.meta,
          ...generateInitialMetaForShape(shape),
          approved: getIsApproved(shape),
          ...(shouldAddAutoModerationTimestamp(shape) ? { submittedForModerationAt: Date.now() } : {}),
        },
      }

      if (shape.type === 'note' && (updatedShape as TLNoteShape).props.color === 'black') {
        // For some weird reason, tldraw decides to init notes with the color 'black' when it is yellow
        ;(updatedShape as TLNoteShape).props.color = 'yellow'
      }

      // Modify geo shape
      if (shape.type === 'geo' && shape.meta.isFromTemplate !== true) {
        const geoShape = shape as TLGeoShape
        if (geoShape.props.geo === 'rhombus' || geoShape.props.geo === 'oval') {
          // We want the parallelogram (officially rhombus) and oval to have the same size as the speech bubble.
          ;(updatedShape as TLGeoShape).props.h = DEFAULT_SPEECH_BUBBLE_HEIGHT - LABEL_PADDING * 2
          ;(updatedShape as TLGeoShape).props.w = DEFAULT_SPEECH_BUBBLE_WIDTH - LABEL_PADDING * 2
        }
      }

      return updatedShape
    }

    // `meta.cb` won't be `null` when:
    //   1. The shape is being copied/duplicated.
    //   2. The shape was rejected approval (deleted), then recreated when the admin undoes it.
    // To assign correct ownership when copying/duplicating shapes, we need to check the source
    // of action, the current user's role, and the shape approval status.

    const isShapeApproved = shape.meta.approved === true
    const changeShapeOwnerToCurrentUser = (): typeof shape => {
      const updatedShape = cloneDeep(shape)
      updatedShape.meta.cb = surfaceStore.user.id
      return updatedShape
    }

    // Case 1: source is user, shape is approved, current user is admin
    // Case 2: source is user, shape is approved, current user is non-admin
    // -> Since the shape is already approved, whoever copies it owns the new shape.
    if (source === 'user' && isShapeApproved) {
      return changeShapeOwnerToCurrentUser()
    }

    // Case 3: source is user, shape is unapproved, current user is admin
    // Case 3.1: admin undoes a rejected shape
    // -> Shouldn't change the owner of the shape since it was created by a different user.
    // Case 3.2: admin copies the unapproved shape
    // -> We'd love to change the ownership of this shape to the admin, but we can't differentiate
    // between this case and 3.1, so we attribute the new shape to the user of the original shape.
    // This is an rare case so we'll leave it like so for now.
    if (source === 'user' && !isShapeApproved && whiteboardPermissionsStore.canIAdminister) {
      return shape
    }

    // Case 4: source is user, shape is unapproved, current user is non-admin
    // -> The user just copied the shape, they should own the new shape.
    if (source === 'user' && !isShapeApproved && !whiteboardPermissionsStore.canIAdminister) {
      return changeShapeOwnerToCurrentUser()
    }

    // Case 5: source is remote
    // -> We accept remote changes as they are. Logic is already handled in the origin client.

    return shape
  })

  editor.sideEffects.registerAfterCreateHandler('shape', (shape, source) => {
    // When editing the style from a padlet-speech-bubble to a geo shape or vice versa, we create a new shape
    // with the updated style and delete the original shape. This cleanup removes the original
    // shape after the new one is created.
    const isUpdatingPadletSpeechBubbleOrGeoStyle =
      (shape.type === 'padlet-speech-bubble' || shape.type === 'geo') && shape.meta.updatingShapeId != null
    if (source === 'user' && isUpdatingPadletSpeechBubbleOrGeoStyle) {
      editor.deleteShape(shape.meta.updatingShapeId as TLShapeId)
      deleteShapeMetadata(shape.id, ['updatingShapeId'])
      return
    }

    if (source === 'user') {
      // If the created shape is not allowed (based on Allowed tools settings), delete it.
      // NOTE:
      // - We don't delete group shapes because they are used for grouping objects.
      // - We don't delete shapes that are created as the result of duplicating a frame, even if
      //   the shape types are now disallowed.
      if (
        !editor.isShapeOfType(shape, 'group') &&
        (shape as TLShape).meta.isFromDuplicatedFrame !== true &&
        !whiteboardToolsStore.wasShapeCreatedFromAllowedTool(shape) &&
        !isUpdatingPadletSpeechBubbleOrGeoStyle
      ) {
        // NOTE: We can't delete this shape synchronously since some internal Tldraw logic  relies
        // on the shape still being present. Delay it using `setTimeout`.
        setTimeout(() => {
          const freshShape = editor.getShape((shape as TLShape).id)
          if (freshShape != null) {
            editor.deleteShape(freshShape.id)
          }
        }, 0)
        return
      }

      // After a shape is created, also update attribution of the page it belongs to.
      const updatedPage = updatePageAttribution(editor.getCurrentPage())
      editor.updatePage(updatedPage)
    }

    if (source === 'remote') {
      if (!doesShapeHaveAutoModerationResult(shape)) {
        // Always trigger a new timeout on shape creation
        void startModerationTimeout(shape)
      }
    }

    if (isAppUsing('sandboxRetainActiveTool') && source === 'user' && whiteboardToolbarStore.xSecondaryMenu) {
      // Close secondary menu after a shape is created
      whiteboardToolbarStore.xSecondaryMenu = false
    }

    // Only proceed if grouping objects is enabled, the source is a user action, and the shape is a group
    if (source !== 'user' || !editor.isShapeOfType(shape, 'group')) {
      return shape
    }

    return shape
  })

  editor.store.sideEffects.registerAfterChangeHandler('instance_page_state', (prev, next) => {
    // No mutations for the shape being changed in afterChangeHandlers
    // Perform side effects here instead

    if (prev.editingShapeId == null && next.editingShapeId != null) {
      // Here when the user starts editing the shape
      currentEditingShapeId.value = cloneDeep(next.editingShapeId)
      const nextShape = editor.getShape(next.editingShapeId)
      originalShapeTextContent.value = nextShape != null ? getTextContent(nextShape) : null
    } else if (prev.editingShapeId != null && next.editingShapeId == null) {
      // Here when the user is done editing the shape by either CMD+ENTER / ESC key / onBlur.
      const shape = editor.getShape(prev.editingShapeId)
      const currentText = shape != null ? getTextContent(shape) : null
      if (
        shape?.meta.submittedForModerationAt != null &&
        isShapeSubjectedToTextModeration(shape) && // Only some shape types require text moderation
        currentText != null && // Must have text to moderate
        currentText !== originalShapeTextContent.value // If the text hasn't changed, we don't need to moderate
      ) {
        void ContentModerationApi.moderateSandboxText(surfaceStore.wallHashid, { shape_id: shape.id })
      }
      currentEditingShapeId.value = null
      originalShapeTextContent.value = null
    }
  })

  editor.sideEffects.registerBeforeChangeHandler('shape', (prev, next, source) => {
    if (source !== 'user') return next

    // Disable editing shapes if non-editor and not shape creator
    if (!whiteboardPermissionsStore.canIEditFrame(editor.getCurrentPageId()) && next.meta.cb !== surfaceStore.user.id) {
      return prev
    }

    /**
     * Migrations for meta fields
     *
     * Any time new fields are added to meta, make sure to ensure their migration is handled here
     */

    let updatedShape = next

    // Claim ownership of the shape if it doesn't have a createdBy field
    if (updatedShape.meta.cb == null) {
      updatedShape = {
        ...updatedShape,
        meta: {
          ...updatedShape.meta,
          cb: surfaceStore.user.id,
          ca: Date.now(),
          lmb: surfaceStore.user.id,
          lma: Date.now(),
        },
      }
    } else {
      updatedShape = {
        ...updatedShape,
        meta: {
          ...updatedShape.meta,
          lmb: surfaceStore.user.id,
          lma: Date.now(),
        },
      }
    }

    // We don't want to allow users to get a shape approved, and then be able to put whatever text they want in it
    const hasTextChanged = getTextContent(prev) !== getTextContent(updatedShape)

    if (hasTextChanged) {
      updatedShape = {
        ...updatedShape,
        meta: {
          ...updatedShape.meta,
          approved: getIsApproved(updatedShape),
          ...(shouldAddAutoModerationTimestamp(updatedShape) ? { submittedForModerationAt: Date.now() } : {}),
          moderationViolations: [],
        },
      }
    }

    // Handle linking inheritance for grouped shapes
    // If the shape is part of a group or is a group itself, we need to ensure that the linking information is properly propagated
    if (isShapeChildOfGroup(updatedShape) || editor.isShapeOfType(updatedShape, 'group')) {
      const parent = editor.getShape(updatedShape.parentId)
      const parentLinking = parent != null ? getLinkingFromShape(parent) : null

      if (parentLinking?.target != null) {
        // Inherit the linking from the parent
        updatedShape.meta.linking = parentLinking.toString()

        // If the shape itself is a group, propagate the linking to all its children
        if (editor.isShapeOfType(updatedShape, 'group')) {
          upsertLinkingToAllShapesInGroup(editor, updatedShape.id, parentLinking)
        }
      }
    }

    // After a group is created, tldraw will assign the group's ID to all child shapes
    // We need to propagate the special effects from the parent group to the child shapes
    // This is to ensure that when a group is created, all its child shapes inherit the special effects from the parent group
    if (isShapeChildOfGroup(updatedShape) || editor.isShapeOfType(updatedShape, 'group')) {
      const parent = editor.getShape(updatedShape.parentId)

      // TODO: combine these two if statements after launching both sandboxVisualFx and sandboxSoundFx
      if (isAppUsing('sandboxVisualFx') && parent != null) {
        const parentSoundEffects = (parent?.meta.sound_effect as ShapeSpecialEffects['sound_effect']) ?? null
        if (parentSoundEffects != null) {
          updatedShape.meta.sound_effect = parentSoundEffects
        }
        if (editor.isShapeOfType(updatedShape, 'group')) {
          updateSpecialEffectsInAllShapesInGroup(updatedShape.id, {
            soundEffectId: parentSoundEffects?.effect_id ?? null,
          })
        }
      }

      if (isAppUsing('sandboxVisualFx') && parent != null) {
        const parentVisualEffects = (parent?.meta.visual_effect as ShapeSpecialEffects['visual_effect']) ?? null
        if (parentVisualEffects != null) {
          updatedShape.meta.visual_effect = parentVisualEffects
        }

        if (editor.isShapeOfType(updatedShape, 'group')) {
          updateSpecialEffectsInAllShapesInGroup(updatedShape.id, {
            visualEffectId: parentVisualEffects?.effect_id ?? null,
          })
        }
      }
    }

    // Freeze/unfreeze all shapes in a group
    if (editor.isShapeOfType(updatedShape, 'group')) {
      if (!prev.isLocked && updatedShape.isLocked) {
        freezeAllShapesInGroup(editor, updatedShape.id)
      } else if (prev.isLocked && !updatedShape.isLocked) {
        unfreezeAllShapesInGroup(editor, updatedShape.id)
      }
    }

    return updatedShape
  })

  editor.sideEffects.registerAfterChangeHandler('shape', (prev, next, source) => {
    if (source === 'remote') {
      if (!doesShapeHaveAutoModerationResult(next)) {
        const prevText = getTextContent(prev)
        const nextText = getTextContent(next)
        if (nextText !== prevText) {
          // If the text has changed, we need to start a new moderation timeout
          void startModerationTimeout(next)
        }
      }

      // When shapes are updated remotely, preload the active special effects
      const prevMeta = prev.meta as ShapeSpecialEffects
      const nextMeta = next.meta as ShapeSpecialEffects
      if (isAppUsing('sandboxSoundFx') && !isEqual(prevMeta.sound_effect, nextMeta.sound_effect)) {
        void whiteboardSpecialEffectsStore.fetchActiveSoundEffectsAndPreload()
      }
      if (isAppUsing('sandboxVisualFx') && !isEqual(prevMeta.visual_effect, nextMeta.visual_effect)) {
        void whiteboardSpecialEffectsStore.preloadActiveVisualEffects()
      }
    }

    if (source !== 'user') return

    // After a shape is updated, also update attribution of the page it belongs to.
    const updatedPage = updatePageAttribution(editor.getCurrentPage())
    editor.updatePage(updatedPage)

    // Persist last used tool properties based on shape style changes
    const toolId = SHAPE_TYPE_TO_TOOL_ID[next.type]
    if (toolId != null) {
      persistToolPropertiesFromShapeChange(toolId, prev, next)
    }
  })

  /**
   * PREVENTING CHANGES TO SHAPES
   */
  editor.sideEffects.registerBeforeDeleteHandler('shape', (shape, source) => {
    if (source !== 'user') return

    // If someone has editor permissions to a frame, they can delete any shape in it.
    // If they don't have editor permissions to the frame, they can only delete the shapes they created.
    if (!whiteboardPermissionsStore.canIEditFrame(editor.getCurrentPageId())) {
      if (shape.meta.cb !== surfaceStore.user.id) {
        return false
      }
    }

    return undefined
  })

  editor.sideEffects.registerAfterDeleteHandler('shape', (_shape, source) => {
    if (source !== 'user') return

    // After a shape is deleted, also update attribution of the page it belongs to.
    const updatedPage = updatePageAttribution(editor.getCurrentPage())
    editor.updatePage(updatedPage)
  })

  function updateExpandSelectionOutlinePx<T extends TLUnknownShape>(shapeUtil: ShapeUtil<T>): void {
    const shapeType = (shapeUtil.constructor as any).type
    if (shapeUtil == null || !('expandSelectionOutlinePx' in shapeUtil)) {
      return
    }
    if (shapeType === 'note') {
      return
    }
    let expandSelectionOutlinePx: (shape: T) => number = () => 8
    if (shapeType === 'text') {
      expandSelectionOutlinePx = (_shape: T): number => 0
    }
    if (shapeType === 'geo') {
      expandSelectionOutlinePx = (shape: T) => {
        const geoShape = shape as TLGeoShape
        const isWhiteRectangleGeo = geoShape.props.color === 'white' && geoShape.props.geo === 'rectangle'
        // const isWhiteRectangleGeo = false // uncomment to disable the special case
        const isCloudGeo = geoShape.props.geo === 'cloud'

        if (isWhiteRectangleGeo) {
          return 4
        }
        if (isCloudGeo) {
          return 6
        }
        return 8
      }
    }
    ;(shapeUtil as any).expandSelectionOutlinePx = expandSelectionOutlinePx
  }
  // Update to shapeUtils to hide editing overlays for shapes that are not created by the user
  // and prevent editing shapes that are not created by the user.
  Object.values(editor.shapeUtils).forEach((shapeUtil) => {
    if (shapeUtil == null) return
    updateExpandSelectionOutlinePx(shapeUtil)

    // Replace callback functions in shapeUtils for checking when to hide the editing overlays (resize handles, rotate handle, etc.)
    // Since user can't edit (because of sideEffects.registerBeforeChangeHandler), it's better UX to hide the editing overlays
    // for shapes that are not created by the user when the shape is selected.
    const HIDE_HANDLERS_TO_REPLACE = [
      'hideSelectionBoundsFg',
      'hideSelectionBoundsBg',
      'hideResizeHandles',
      'hideRotateHandle',
    ]
    HIDE_HANDLERS_TO_REPLACE.forEach((handler) => {
      const oldHandler = shapeUtil[handler]
      shapeUtil[handler] = function (shape: TLShape) {
        if (handler === 'hideSelectionBoundsFg' && !isShapeApproved(shape)) {
          return true
        }
        if (
          !whiteboardPermissionsStore.canIEditFrame(editor.getCurrentPageId()) &&
          shape.meta.cb !== surfaceStore.user.id
        ) {
          return true
        }
        return oldHandler.call(this, shape)
      }
    })

    // Replace canEdit callback function in shapeUtils for checking if the shape can be edited.
    // This combines with the changes in patches/tldraw+2.1.4.patch prevent double-click to edit text labels for shapes that are not created by the user.
    const CAN_EDIT_CHECKS_TO_REPLACE = ['canEdit', 'canEditInReadOnly']
    CAN_EDIT_CHECKS_TO_REPLACE.forEach((handler) => {
      const oldHandler = shapeUtil[handler]
      shapeUtil[handler] = function (shape: TLShape) {
        if (
          !whiteboardPermissionsStore.canIEditFrame(editor.getCurrentPageId()) &&
          shape.meta.cb !== surfaceStore.user.id
        ) {
          return false
        }
        return oldHandler.call(this, shape)
      }
    })
  })
}
export { useWhiteboardShapeHandlers }
